import { useState } from 'react'
import { Alert, Form, Modal, Select, Typography } from 'antd'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import type { Subscriber } from '@/features/subscriber/subscriberApi'
import { branchLabel, setSubscriberBranch } from './branchApi'
import { useActiveBranches } from './useActiveBranches'

/** Abonent filialini almashtirish; tashkilot doirasi filialdan chiqarishi ham mumkin */
export function ChangeBranchModal({
  subscriber,
  open,
  canClear,
  onClose,
}: {
  subscriber: Subscriber
  open: boolean
  canClear: boolean
  onClose: () => void
}) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { options } = useActiveBranches()
  const [branchId, setBranchId] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const current = subscriber.branchNodeId ?? null

  const submit = async () => {
    setSaving(true)
    setError(null)
    try {
      await setSubscriberBranch(subscriber.id, branchId)
      void queryClient.invalidateQueries({ queryKey: queryKeys.subscribers.all })
      onClose()
    } catch (err) {
      setError(translateError(err, t))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal
      open={open}
      title={t('branch.changeTitle', { login: subscriber.login })}
      onCancel={onClose}
      onOk={() => void submit()}
      okText={t('common.save')}
      cancelText={t('common.cancel')}
      confirmLoading={saving}
      okButtonProps={{ disabled: branchId === current || (!branchId && !canClear) }}
      destroyOnHidden
      afterOpenChange={(visible) => {
        if (!visible) return
        setError(null)
        setBranchId(current)
      }}
    >
      {error ? <Alert type="error" showIcon style={{ marginBottom: 16 }} message={error} /> : null}
      <Typography.Paragraph type="secondary">{t('branch.changeHint')}</Typography.Paragraph>
      <Form layout="vertical" disabled={saving}>
        <Form.Item label={t('branch.label')}>
          <Select<string>
            showSearch
            allowClear={canClear}
            optionFilterProp="label"
            placeholder={t('branch.choose')}
            value={branchId ?? undefined}
            onChange={(value) => setBranchId(value ?? null)}
            options={options.map((b) => ({ value: b.id, label: branchLabel(b) }))}
          />
        </Form.Item>
      </Form>
    </Modal>
  )
}
